<template>
  <div>
    <v-card flat class="history-container">
      <v-card-title class="d-flex align-center pb-2">
        <v-icon large color="primary" class="mr-2">mdi-history</v-icon>
        <span class="text-h6">
          Vehicle History
          <span
            v-if="caseState.casefile && caseState.casefile.vehicleNo"
            class="ml-2 grey--text text--darken-2"
          >
            ({{ caseState.casefile.vehicleNo.toUpperCase() }})
          </span>
        </span>
        <v-spacer></v-spacer>
        <!-- Hide chip if TBA/NONE -->
        <v-chip
          v-if="!isVehicleNoTbaOrNone && filteredCases.length > 0"
          color="orange"
          small
          class="font-weight-medium"
        >
          {{ filteredCases.length }} Previous Cases
        </v-chip>
      </v-card-title>

      <v-divider></v-divider>

      <!-- Always show "No Previous Cases Found" if vehicleNo is TBA/NONE -->
      <template v-if="isVehicleNoTbaOrNone">
        <v-card-text class="text-center pa-8">
          <v-icon size="64" color="grey lighten-1">mdi-car-off</v-icon>
          <h3 class="mt-4 grey--text text--darken-1">
            No Previous Cases Found
          </h3>
          <p class="subtitle-1 grey--text">
            This is the first case for this vehicle
          </p>
    </v-card-text>
        <v-card-actions class="d-flex align-center justify-space-between pa-2">
          <div class="grey--text">Total: {{ total }}</div>

          <v-pagination
            v-model="page"
            :length="Math.max(1, Math.ceil(total / pageSize))"
            :total-visible="7"
            :disabled="loadingLocal"
            @input="fetchHistory(page, pageSize)"
          ></v-pagination>

          <v-select
            :items="[10,20,30,40,50,100]"
            v-model="pageSize"
            dense
            hide-details
            style="width:120px"
            @change="onPageSizeChange"
            :disabled="loadingLocal"
            label="Rows"
          ></v-select>
        </v-card-actions>
      </template>
      <template v-else>
        <!-- Loading State -->
        <div v-if="loadingLocal" class="pa-4">
          <v-skeleton-loader
            v-for="n in 3"
            :key="n"
            type="list-item-two-line"
            class="mb-4"
          ></v-skeleton-loader>
        </div>

        <!-- No History State -->
        <v-card-text
          v-else-if="!filteredCases.length"
          class="text-center pa-8"
        >
          <v-icon size="64" color="grey lighten-1">mdi-car-off</v-icon>
          <h3 class="mt-4 grey--text text--darken-1">
            No Previous Cases Found
          </h3>
          <p class="subtitle-1 grey--text">
            This is the first case for this vehicle
          </p>
        </v-card-text>

        <!-- History List -->
        <v-card-text v-else class="pa-4">
          <v-row>
            <v-col
              v-for="item in filteredCases"
              :key="item.id"
              cols="12"
              sm="6"
              md="4"
            >
              <v-card
                outlined
                hover
                class="history-card"
                @click="showDetail(item)"
              >
                <v-card-text>
                  <div class="d-flex align-center mb-2">
                    <v-icon color="primary" small class="mr-2"
                      >mdi-file-document-outline</v-icon
                    >
                    <span class="text-subtitle-2"
                      >AA/{{ getRefNumber(item) }}</span
                    >
                  </div>

                  <div class="d-flex align-center">
                    <v-icon small color="grey" class="mr-2"
                      >mdi-calendar</v-icon
                    >
                    <span class="caption grey--text">{{
                      formatDate(item.createdAt)
                    }}</span>
                  </div>

                  <v-chip
                    x-small
                    :color="getStatusColor(item.fileStatus)"
                    class="mt-2"
                    text-color="white"
                  >
                    {{ getStatusText(item.fileStatus) }}
                  </v-chip>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
        </v-card-text>
      </template>
    </v-card>

    <!-- Detail Dialog -->
    <v-dialog v-model="dialogDetail" width="700">
      <v-card>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="blue darken-1" text @click="dialogDetail = false">
            Close
          </v-btn>
        </v-card-actions>
        <v-card-title>
          <v-spacer></v-spacer>
          <span class="caption text-decoration-overline"
            >Details of AA/<span
              v-for="(dept, index) in filterDept(caseDetail.refType)"
              :key="index"
              >{{ dept.name }}
              <span
                v-for="(subRef, index) in filterSubDept(caseDetail.subRefType)"
                :key="index"
                >/{{ subRef.subCode }}</span
              > </span
            >/{{ caseDetail.id }}/{{ yearOnly(caseDetail.createdAt) }}/<span
              v-for="branch in filterBranches(caseDetail.branch)"
              :key="branch.id"
              >{{ branch.brCode }}
            </span>
          </span>
        </v-card-title>
        <v-simple-table dense>
          <template v-slot:default>
            <thead>
              <tr>
                <th class="text-center font-weight-black">Name</th>
                <th class="text-left font-weight-black">Description</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="caseDetail.fileStatus">
                <td class="text-center">Status</td>
                <td
                  v-for="fileStatus in filterStages(caseDetail.fileStatus)"
                  :key="fileStatus.id"
                  class="text-left font-italic font-weight-black"
                >
                  {{ fileStatus.name }}
                </td>
              </tr>
              <tr v-if="caseDetail.createdAt">
                <td class="text-center">Created On:</td>
                <td class="text-left font-italic">
                  {{ formatDate(caseDetail.createdAt) }}
                </td>
              </tr>
              <tr v-if="caseDetail.createdAt">
                <td class="text-center">Created By:</td>
                <td class="text-left font-italic">
                  {{ filterUser(caseDetail.createdBy) }}
                </td>
              </tr>
              <tr v-if="caseDetail.transferDate">
                <td class="text-center">Transfer Date</td>
                <td class="text-left font-italic">
                  {{ formatDate(caseDetail.transferDate) }}
                </td>
              </tr>
              <tr v-if="caseDetail.transferDate">
                <td class="text-center">Transfered By:</td>
                <td class="text-left font-italic">
                  {{ filterUser(caseDetail.transferBy) }}
                </td>
              </tr>
              <tr v-if="caseDetail.dateOfCancel">
                <td class="text-center">Cancellation Date</td>
                <td class="text-left font-italic">
                  {{ formatDate(caseDetail.dateOfCancel) }}
                </td>
              </tr>
              <tr v-if="caseDetail.dateOfCancel">
                <td class="text-center">Cancellation By:</td>
                <td class="text-left font-italic">
                  {{ filterUser(caseDetail.cancelBy) }}
                </td>
              </tr>
              <tr v-if="caseDetail.caseFrom">
                <td class="text-center">Type</td>
                <td class="text-left font-italic">{{ caseDetail.caseFrom }}</td>
              </tr>

              <tr v-if="caseDetail.refType">
                <td class="text-center">Department</td>
                <td class="text-left font-italic">
                  {{ filterDept(caseDetail.refType)[0].name }}, ({{
                    filterDept(caseDetail.refType)[0].description
                  }})
                </td>
              </tr>
              <tr v-if="caseDetail.subRefType">
                <td class="text-center">File Classification</td>
                <td class="text-left font-italic">
                  {{ filterSubDept(caseDetail.subRefType)[0].subCode }}
                </td>
              </tr>
              <tr v-if="caseDetail.branch">
                <td class="text-center">Branch</td>
                <td class="text-left font-italic">
                  {{ filterBranches(caseDetail.branch)[0].brCode }}, ({{
                    filterBranches(caseDetail.branch)[0].name
                  }})
                </td>
              </tr>
              <tr v-if="caseDetail.insurer">
                <td class="text-center">Insurer</td>
                <td class="text-left font-italic">
                  {{ filterIns(caseDetail.insurer)[0].name }}
                </td>
              </tr>
              <tr v-if="caseDetail.handler">
                <td class="text-center">Handler</td>
                <td class="text-left font-italic">
                  {{ filterHandler(caseDetail.handler)[0].name }}, ({{
                    filterHandler(caseDetail.handler)[0].email
                  }})
                </td>
              </tr>
              <tr v-if="caseDetail.adjuster">
                <td class="text-center">Adjuster</td>
                <td class="text-left font-italic">
                  {{ filterUser(caseDetail.adjuster) }}
                </td>
              </tr>
              <tr v-if="caseDetail.editor">
                <td class="text-center">Editor</td>
                <td class="text-left font-italic">
                  {{ filterUser(caseDetail.editor) }}
                </td>
              </tr>
              <tr v-if="caseDetail.clerkInCharge">
                <td class="text-center">Clerk In Charge</td>
                <td class="text-left font-italic">
                  {{ filterUser(caseDetail.clerkInCharge) }}
                </td>
              </tr>
              <tr v-if="caseDetail.claimNo">
                <td class="text-center">Insurer's Ref No.</td>
                <td class="text-left font-italic">{{ caseDetail.claimNo }}</td>
              </tr>
              <tr v-if="caseDetail.vehicleNo">
                <td class="text-center">Vehicle No.</td>
                <td class="text-left font-italic">
                  {{ caseDetail.vehicleNo.toUpperCase() }}
                </td>
              </tr>
              <tr v-if="caseDetail.dateOfAssign">
                <td class="text-center">Date Of Assignment</td>
                <td class="text-left font-italic">
                  {{ formatDate(caseDetail.dateOfAssign) }}
                </td>
              </tr>
              <tr v-if="caseDetail.dateOfLoss">
                <td class="text-center">Date Of Loss</td>
                <td class="text-left font-italic">
                  {{ formatDate(caseDetail.dateOfLoss) }}
                </td>
              </tr>
              <tr v-if="caseDetail.stateOfLoss">
                <td class="text-center">State Of Loss</td>
                <td class="text-left font-italic">
                  {{ filterStates(caseDetail.stateOfLoss)[0].stCode }}, ({{
                    filterStates(caseDetail.stateOfLoss)[0].name
                  }})
                </td>
              </tr>
              <tr v-if="caseDetail.placeOfLoss">
                <td class="text-center">Place Of Loss</td>
                <td class="text-left font-italic">
                  {{ caseDetail.placeOfLoss }}
                </td>
              </tr>
              <tr v-if="caseDetail.insuredName">
                <td class="text-center">Insured Name</td>
                <td class="text-left font-italic">
                  {{ caseDetail.insuredName }}
                </td>
              </tr>
              <tr v-if="caseDetail.insuredIC">
                <td class="text-center">Insured IC No.</td>
                <td class="text-left font-italic">
                  {{ caseDetail.insuredIC }}
                </td>
              </tr>
              <tr v-if="caseDetail.InsuredTel">
                <td class="text-center">Insured Tel. No.</td>
                <td class="text-left font-italic">
                  {{ caseDetail.InsuredTel }}
                </td>
              </tr>
              <tr v-if="caseDetail.insuredEmail">
                <td class="text-center">Insured Email</td>
                <td class="text-left font-italic">
                  {{ caseDetail.insuredEmail }}
                </td>
              </tr>
              <tr v-if="caseDetail.insuredDriverName">
                <td class="text-center">Insured Driver Name</td>
                <td class="text-left font-italic">
                  {{ caseDetail.insuredDriverName }}
                </td>
              </tr>
              <tr v-if="caseDetail.insuredDriverIc">
                <td class="text-center">Insured Driver IC No.</td>
                <td class="text-left font-italic">
                  {{ caseDetail.insuredDriverIc }}
                </td>
              </tr>
              <tr v-if="caseDetail.insuredDriverEmail">
                <td class="text-center">Insured Driver Email</td>
                <td class="text-left font-italic">
                  {{ caseDetail.insuredDriverEmail }}
                </td>
              </tr>
              <tr v-if="caseDetail.policeReportNo">
                <td class="text-center">Police Report No.</td>
                <td class="text-left font-italic">
                  {{ caseDetail.policeReportNo }}
                </td>
              </tr>
              <tr v-if="caseDetail.policeReportDate">
                <td class="text-center">Police Report Date</td>
                <td class="text-left font-italic">
                  {{ caseDetail.policeReportDate }}
                </td>
              </tr>
              <tr v-if="caseDetail.policyNo">
                <td class="text-center">Policy Number</td>
                <td class="text-left font-italic">
                  {{ caseDetail.policyNo }}
                </td>
              </tr>
              <tr v-if="caseDetail.coverFrom || caseDetail.coverTo">
                <td class="text-center">Cover Period</td>
                <td class="text-left font-italic">
                  {{ formatDate(caseDetail.coverFrom) }} -
                  {{ formatDate(caseDetail.coverTo) }}
                </td>
              </tr>
              <tr v-if="caseDetail.insComment">
                <td class="text-center">Insurer's Comment</td>
                <td class="text-left font-italic">
                  {{ caseDetail.insComment }}
                </td>
              </tr>
              <tr v-if="caseDetail.solicitorName">
                <td class="text-center">Solicitor Name</td>
                <td class="text-left font-italic">
                  {{ caseDetail.solicitorName }}
                </td>
              </tr>
              <tr v-if="caseDetail.solicitorTel">
                <td class="text-center">Solicitor Tel.</td>
                <td class="text-left font-italic">
                  {{ caseDetail.solicitorTel }}
                </td>
              </tr>
              <tr v-if="caseDetail.solicitorEmail">
                <td class="text-center">Solicitor Email</td>
                <td class="text-left font-italic">
                  {{ caseDetail.solicitorEmail }}
                </td>
              </tr>
              <tr v-if="caseDetail.counselName">
                <td class="text-center">Counsel Name</td>
                <td class="text-left font-italic">
                  {{ caseDetail.counselName }}
                </td>
              </tr>
              <tr v-if="caseDetail.counselTel">
                <td class="text-center">Counsel Tel</td>
                <td class="text-left font-italic">
                  {{ caseDetail.counselTel }}
                </td>
              </tr>
              <tr v-if="caseDetail.counselEmail">
                <td class="text-center">Counsel Email</td>
                <td class="text-left font-italic">
                  {{ caseDetail.counselEmail }}
                </td>
              </tr>
              <tr v-if="caseDetail.thirdPartyClaimantName">
                <td class="text-center">Third Party Claimant Name</td>
                <td class="text-left font-italic">
                  {{ caseDetail.thirdPartyClaimantName }}
                </td>
              </tr>
              <tr v-if="caseDetail.thirdPartyClaimantIc">
                <td class="text-center">Third Party Claimant IC No.</td>
                <td class="text-left font-italic">
                  {{ caseDetail.thirdPartyClaimantIc }}
                </td>
              </tr>
              <tr v-if="caseDetail.thirdPartyClaimantTel">
                <td class="text-center">Third Party Claimant Tel No.</td>
                <td class="text-left font-italic">
                  {{ caseDetail.thirdPartyClaimantTel }}
                </td>
              </tr>
              <tr v-if="caseDetail.thirdPartyClaimantEmail">
                <td class="text-center">Third Party Claimant Email</td>
                <td class="text-left font-italic">
                  {{ caseDetail.thirdPartyClaimantEmail }}
                </td>
              </tr>
              <tr v-if="caseDetail.thirdPartyClaimantVehicleNo">
                <td class="text-center">Third Party Claimant Vehicle No.</td>
                <td class="text-left font-italic">
                  {{ caseDetail.thirdPartyClaimantVehicleNo }}
                </td>
              </tr>
              <tr v-if="caseDetail.thirdPartyClaimantVehicleModel">
                <td class="text-center">Third Party Claimant Vehicle Model</td>
                <td class="text-left font-italic">
                  {{ caseDetail.thirdPartyClaimantVehicleModel }}
                </td>
              </tr>
              <tr v-if="caseDetail.thirdPartyClaimantVehicleSolicitor">
                <td class="text-center">Third Party Claimant Solicitor</td>
                <td class="text-left font-italic">
                  {{ caseDetail.thirdPartyClaimantVehicleSolicitor }}
                </td>
              </tr>
              <tr v-if="caseDetail.thirdPartyClaimantVehicleSolicitorEmail">
                <td class="text-center">
                  Third Party Claimant Solicitor Email
                </td>
                <td class="text-left font-italic">
                  {{ caseDetail.thirdPartyClaimantVehicleSolicitorEmail }}
                </td>
              </tr>

              <tr v-if="caseDetail.applyDocDate">
                <td class="text-center">Document Application Date</td>
                <td class="text-left font-italic">
                  {{ formatDate(caseDetail.applyDocDate) }}
                </td>
              </tr>
              <tr v-if="caseDetail.applyDocDate">
                <td class="text-center">Document Application By:</td>
                <td class="text-left font-italic">
                  {{ filterUser(caseDetail.applyDocBy) }}
                </td>
              </tr>
              <tr v-if="caseDetail.dateOfReg">
                <td class="text-center">Branch Assigning Date</td>
                <td class="text-left font-italic">
                  {{ formatDate(caseDetail.dateOfReg) }}
                </td>
              </tr>
              <tr v-if="caseDetail.dateOfReg">
                <td class="text-center">Branch Assigned By:</td>
                <td class="text-left font-italic">
                  {{ filterUser(caseDetail.assignBranchBy) }}
                </td>
              </tr>
              <tr v-if="caseDetail.dateOfAdj">
                <td class="text-center">Adjuster Assigning Date</td>
                <td class="text-left font-italic">
                  {{ formatDate(caseDetail.dateOfAdj) }}
                </td>
              </tr>
              <tr v-if="caseDetail.dateOfAdj">
                <td class="text-center">Adjuster Assigning By:</td>
                <td class="text-left font-italic">
                  {{ filterUser(caseDetail.assignAdjBy) }}
                </td>
              </tr>
              <tr v-if="caseDetail.dateStartInv">
                <td class="text-center">Start Investigation Date</td>
                <td class="text-left font-italic">
                  {{ formatDate(caseDetail.dateStartInv) }}
                </td>
              </tr>
              <tr v-if="caseDetail.dateEndInv">
                <td class="text-center">Complete Investigation Date</td>
                <td class="text-left font-italic">
                  {{ formatDate(caseDetail.dateEndInv) }}
                </td>
              </tr>
              <tr v-if="caseDetail.dateOfEdi">
                <td class="text-center">Editor Assigned Date</td>
                <td class="text-left font-italic">
                  {{ formatDate(caseDetail.dateOfEdi) }}
                </td>
              </tr>
              <tr v-if="caseDetail.dateOfEdi">
                <td class="text-center">Editor Assigned By:</td>
                <td class="text-left font-italic">
                  {{ filterUser(caseDetail.assignEdiBy) }}
                </td>
              </tr>
              <tr v-if="caseDetail.dateStartEdi">
                <td class="text-center">Start Editing Date</td>
                <td class="text-left font-italic">
                  {{ formatDate(caseDetail.dateStartEdi) }}
                </td>
              </tr>
              <tr v-if="caseDetail.dateEndEdi">
                <td class="text-center">Complete Editing Date</td>
                <td class="text-left font-italic">
                  {{ formatDate(caseDetail.dateEndEdi) }}
                </td>
              </tr>
              <tr v-if="caseDetail.dateOfApproval">
                <td class="text-center">Approval Date</td>
                <td class="text-left font-italic">
                  {{ formatDate(caseDetail.dateOfApproval) }}
                </td>
              </tr>
              <tr v-if="caseDetail.dateOfApproval">
                <td class="text-center">Approval By:</td>
                <td class="text-left font-italic">
                  {{ filterUser(caseDetail.approvedBy) }}
                </td>
              </tr>
              <tr v-if="caseDetail.dateAssignFormatting">
                <td class="text-center">Clerk Assigned Date(Formatting)</td>
                <td class="text-left font-italic">
                  {{ formatDate(caseDetail.dateAssignFormatting) }}
                </td>
              </tr>
              <tr v-if="caseDetail.dateAssignFormatting">
                <td class="text-center">Clerk Assigned (Formatting) By:</td>
                <td class="text-left font-italic">
                  {{ filterUser(caseDetail.assignClerkBy) }}
                </td>
              </tr>
              <tr v-if="caseDetail.dateStartFormatting">
                <td class="text-center">Start Formatting Date</td>
                <td class="text-left font-italic">
                  {{ formatDate(caseDetail.dateStartFormatting) }}
                </td>
              </tr>
              <tr v-if="caseDetail.dateEndFormatting">
                <td class="text-center">Complete Formatting Date</td>
                <td class="text-left font-italic">
                  {{ formatDate(caseDetail.dateEndFormatting) }}
                </td>
              </tr>
              <tr v-if="caseDetail.dateFinal">
                <td class="text-center">Invoising & Finalisation Date</td>
                <td class="text-left font-italic">
                  {{ formatDate(caseDetail.dateFinal) }}
                </td>
              </tr>
              <tr v-if="caseDetail.dateFinal">
                <td class="text-center">Invoising & Finalisation By:</td>
                <td class="text-left font-italic">
                  {{ filterUser(caseDetail.finalBy) }}
                </td>
              </tr>
              <tr v-if="caseDetail.dateFinal">
                <td class="text-center">Invoice Nombor</td>
                <td class="text-left font-italic">
                  {{ caseDetail.invNo }}
                </td>
              </tr>
              <tr v-if="caseDetail.dateFinal">
                <td class="text-center">Invoice Fee Note</td>
                <td class="text-left font-italic">
                  {{ caseDetail.invFeeNote }}
                </td>
              </tr>
              <tr v-if="caseDetail.dateFinal">
                <td class="text-center">Invoice Total</td>
                <td class="text-left font-italic">
                  RM {{ caseDetail.invTotal }}
                </td>
              </tr>
              <tr v-if="caseDetail.dateDispatch">
                <td class="text-center">Dispatch Date</td>
                <td class="text-left font-italic">
                  {{ formatDate(caseDetail.dateDispatch) }}
                </td>
              </tr>
              <tr v-if="caseDetail.dateDispatch">
                <td class="text-center">Dispatch By:</td>
                <td class="text-left font-italic">
                  {{ filterUser(caseDetail.dispatchBy) }}
                </td>
              </tr>
              <tr v-if="caseDetail.dateClosed">
                <td class="text-center">Closed Date</td>
                <td class="text-left font-italic">
                  {{ formatDate(caseDetail.dateClosed) }}
                </td>
              </tr>
              <tr v-if="caseDetail.dateClosed">
                <td class="text-center">Closed By:</td>
                <td class="text-left font-italic">
                  {{ filterUser(caseDetail.closedBy) }}
                </td>
              </tr>

              <!-- <tr v-for="(item, index) in caseDetail" :key="index">
                <td>{{ index }}</td>
                <td>{{ item }}</td>
              </tr> -->
            </tbody>
          </template>
        </v-simple-table>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="blue darken-1" text @click="dialogDetail = false">
            Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import CasefileDataService from "../../services/CasefileDataService";
import { mapState } from "vuex";
import moment from "moment";

export default {
  name: "history",
  props: {
    branches: {
      type: Array,
      required: true,
    },
    states: {
      type: Array,
      required: true,
    },
    insurer: {
      type: Array,
      required: true,
    },
    handlers: {
      type: Array,
      required: true,
    },
    refType: {
      type: Array,
      required: true,
    },
    subRefTypes: {
      type: Array,
      required: true,
    },
    members: {
      type: Array,
      required: true,
    },
    stages: {
      type: Array,
      required: true,
    },
    preloadedHistory: {
      type: Array,
      default: () => [],
    },
  },
  data: () => ({
    cases: [],
    dialogDetail: false,
    caseDetail: "",
    caseHistory: [],
    // server-side pagination state
    page: 1,
    pageSize: 50,
    total: 0,
    loadingLocal: false,
  }),

  computed: {
    ...mapState(["caseState"]),
    filteredCases() {
      // Filter out current case and sort by date descending
      if (this.isVehicleNoTbaOrNone) {
        this.$emit("history-count-updated", 0);
        return [];
      }
      const filtered = this.cases
        // normalize id types before comparing to current case id
        .filter((c) => String(c.id) !== String(this.caseState.casefile.id))
        .sort((a, b) => moment(b.createdAt) - moment(a.createdAt));

      // Emit the actual count of filtered cases
      this.$emit("history-count-updated", filtered.length);
      return filtered;
    },
    isVehicleNoTbaOrNone() {
      const vno = this.caseState.casefile?.vehicleNo;
      if (!vno) return true;
      const val = String(vno).trim().toUpperCase();
      return val === "TBA" || val === "NONE";
    },
  },

  methods: {
    formatDate: function (date) {
      if (!date) return "";
      return moment(date).format("DD/MM/YYYY");
    },
    showDetail(item) {
      this.caseDetail = Object.assign({}, item);
      this.dialogDetail = true;
    },
    yearOnly(value) {
      // return moment(value).format("YYYY");
      return moment(value).format("YY");
    },
    filterCase(item) {
      return this.cases.filter((file) => file.id === item);
    },
    filterDept(item) {
      return this.refType.filter((dept) => dept.id === item);
    },
    filterSubDept(item) {
      return this.subRefTypes.filter((subRef) => subRef.id === item);
    },

    filterBranches(item) {
      return this.branches.filter((branch) => branch.id === item);
    },
    filterStates(item) {
      return this.states.filter((state) => state.id === item);
    },
    filterIns(item) {
      return this.insurer.filter((insurer) => insurer.id === item);
    },
    filterStages(item) {
      return this.stages.filter((stage) => stage.stageCode === item);
    },
    filterUser(item) {
      // return this.members.filter((member) => member.id === item);
      const myArray = this.members.filter((member) => member.id === item);
      let result = myArray.map((a) => a.username + ", " + a.email);
      return result[0];
    },
    filterHandler(item) {
      return this.handlers.filter((handler) => handler.id === item);
    },
    showFileStatus(item) {
      const myArray = this.stages.filter((stage) => stage.stageCode === item);
      let result = myArray.map((a) => a.name);
      return result;
    },

    async fetchHistory(page = 1, pageSize = 50) {
      // Two-step fetch to improve perceived performance:
      // 1) Fast fetch without COUNT (needCount=false) to render rows immediately
      // 2) Background fetch with COUNT (needCount=true) to update total and counts
      const vehicleNo = this.caseState.casefile?.vehicleNo;
      if (!vehicleNo) {
        this.cases = [];
        this.total = 0;
        this.$emit("history-count-updated", 0);
        return;
      }

      const v = String(vehicleNo).trim().toUpperCase();
      if (v === "TBA" || v === "NONE") {
        this.cases = [];
        this.total = 0;
        this.$emit("history-count-updated", 0);
        return;
      }

      this.loadingLocal = true;
      this.page = page;
      this.pageSize = pageSize;

      const currentId = this.caseState.casefile?.id;

      try {
        // Step 1: fast fetch without COUNT
        const paramsFast = { page: this.page, pageSize: this.pageSize, vehicleNo, vehicleNoExact: 'true', needCount: false };
        const respFast = await CasefileDataService.getPaginated(paramsFast);
        const payloadFast = respFast && respFast.data !== undefined ? respFast.data : respFast;

        let rowsFast = [];
        if (payloadFast) {
          if (Array.isArray(payloadFast.data)) rowsFast = payloadFast.data;
          else if (Array.isArray(payloadFast.rows)) rowsFast = payloadFast.rows;
          else if (Array.isArray(payloadFast)) rowsFast = payloadFast;
        }

        const filteredFast = rowsFast.filter((r) => String(r.id) !== String(currentId));
        this.cases = filteredFast;
        // temporary total (best-effort) until full count returns
        this.total = filteredFast.length;
        this.$emit('history-count-updated', this.total);

        // Step 2: background fetch with COUNT to update totals and authoritative rows
        (async () => {
          try {
            const paramsFull = { page: this.page, pageSize: this.pageSize, vehicleNo, vehicleNoExact: 'true', needCount: true };
            const respFull = await CasefileDataService.getPaginated(paramsFull);
            const payload = respFull && respFull.data !== undefined ? respFull.data : respFull;

            let rows = [];
            let tot = null;
            if (payload) {
              if (Array.isArray(payload.data)) {
                rows = payload.data;
                if (typeof payload.total !== 'undefined') tot = payload.total;
                else if (typeof payload.totalCount !== 'undefined') tot = payload.totalCount;
              } else if (Array.isArray(payload.rows) && typeof payload.count !== 'undefined') {
                rows = payload.rows;
                tot = payload.count;
              } else if (Array.isArray(payload)) {
                rows = payload;
                tot = payload.length;
              } else if (payload.data && Array.isArray(payload.data)) {
                rows = payload.data;
                tot = payload.total || payload.totalCount || rows.length;
              }
            }

            const filteredRows = rows.filter((r) => String(r.id) !== String(currentId));
            this.cases = filteredRows;

            if (typeof tot === 'number') {
              this.total = Math.max(0, tot - (rows.some(r => String(r.id) === String(currentId)) ? 1 : 0));
            } else {
              this.total = filteredRows.length;
            }
            this.$emit('history-count-updated', this.total);
          } catch (e) {
            // background failure: keep fast results, log for diagnostics
            // avoid disturbing user with errors here
            console.warn('history: background full-count fetch failed', e);
          }
        })();

      } catch (err) {
        // fallback to preloadedHistory if available
        if (this.preloadedHistory && this.preloadedHistory.length > 0) {
          this.cases = [...this.preloadedHistory].filter((c) => String(c.id) !== String(this.caseState.casefile?.id));
          this.total = this.cases.length;
          this.$emit('history-count-updated', this.total);
        } else {
          console.error('history: failed to fetch paginated history', err);
          this.cases = [];
          this.total = 0;
          this.$emit('history-count-updated', 0);
        }
      } finally {
        this.loadingLocal = false;
      }
    },
    onPageSizeChange(val) {
      // reset to first page
      this.page = 1;
      this.fetchHistory(1, Number(val));
    },
    getRefNumber(item) {
      const dept = this.filterDept(item.refType)[0]?.name || "";
      const subRef = this.filterSubDept(item.subRefType)[0]?.subCode || "";
      const branch = this.filterBranches(item.branch)[0]?.brCode || "";
      return `${dept}${subRef ? "/" + subRef : ""}/${item.id}/${this.yearOnly(
        item.createdAt
      )}${branch ? "/" + branch : ""}`;
    },

    getStatusColor(status) {
      const colors = {
        NEW: "success",
        PEND: "warning",
        INVE: "info",
        COMP: "error",
        CLOSED: "grey",
      };
      return colors[status] || "grey";
    },

    getStatusText(status) {
      const stage = this.filterStages(status)[0];
      return stage ? stage.name : status;
    },
  },

  watch: {
    preloadedHistory: {
      immediate: true,
      handler(newHistory) {
        if (newHistory && newHistory.length > 0) {
          this.cases = [...newHistory];
          this.$emit("history-count-updated", this.filteredCases.length);
        }
      },
    },
  },
  mounted() {
    // start with server-side fetch
    this.fetchHistory(this.page, this.pageSize);
  },
};
</script>

<style scoped>
.history-container {
  border-radius: 8px;
  background: #ffffff;
}

.history-card {
  transition: transform 0.2s, box-shadow 0.2s;
  cursor: pointer;
}

.history-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1) !important;
}

/* Add fade transitions */
.v-enter-active,
.v-leave-active {
  transition: opacity 0.3s ease;
}

.v-enter,
.v-leave-to {
  opacity: 0;
}
</style>
